import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EventRound, EventSong, HitsterEvent } from '../types';
import './PrintRound.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PrintRound: React.FC = () => {
  const { eventId, roundId } = useParams<{ eventId: string; roundId: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [round, setRound] = useState<EventRound | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/events/${eventId}`);
        if (res.ok) {
          const data: HitsterEvent = await res.json();
          setEvent(data);
          setRound(data.rounds.find(r => r.id === roundId) ?? null);
        }
      } catch (err) {
        console.error('Failed to load event:', err);
      }
      setLoading(false);
    };
    load();
  }, [eventId, roundId]);

  const handlePrint = () => window.print();

  const handleExportJson = () => {
    if (!event || !round) return;
    const payload = {
      event: { id: event.id, name: event.name, date: event.date },
      round: {
        id: round.id,
        name: round.name,
        order: round.order,
        songs: round.songs
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((s: EventSong) => ({
            order: s.order,
            title: s.title,
            artist: s.artist,
            album: s.album,
            year: s.year,
            duration: s.duration,
            spotifyUri: s.spotifyUri ?? null,
            youtubeVideoId: s.youtubeVideoId ?? null,
            youtubeStartTime: s.youtubeStartTime ?? null,
            youtubeEndTime: s.youtubeEndTime ?? null,
            audioUrl: s.audioUrl ?? null,
            previewUrl: s.previewUrl ?? null,
            imageUrl: s.imageUrl ?? null,
            volumeGain: s.volumeGain ?? null,
            yearSource: s.yearSource ?? null,
          })),
      },
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.name} - ${round.name}.json`
      .replace(/[/\\?%*:|"<>]/g, '-');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBack = () => navigate('/spotify-dashboard');

  if (loading) return <div className="print-round loading">Laden…</div>;
  if (!event || !round) return <div className="print-round error">Ronde niet gevonden</div>;

  const sorted = round.songs
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const sourceLabel = (s: EventSong) => {
    if (s.spotifyUri) return 'Spotify';
    if (s.youtubeVideoId) return 'YouTube';
    if (s.audioUrl) return 'Audio';
    if (s.previewUrl) return 'Preview';
    return '—';
  };

  return (
    <div className="print-round">
      {/* Controls – hidden when printing */}
      <div className="print-round-controls no-print">
        <button className="pr-back-btn" onClick={handleBack} title="Terug">
          ←
        </button>
        <h1>{round.name}</h1>
        <div className="pr-control-buttons">
          <button className="pr-json-btn" onClick={handleExportJson}>
            ⬇ JSON
          </button>
          <button className="pr-print-btn" onClick={handlePrint}>
            🖨️ PDF / Printen
          </button>
        </div>
      </div>

      {/* Printable content */}
      <div className="print-round-content">
        <div className="pr-header">
          <div className="pr-event-name">{event.name}</div>
          <div className="pr-round-name">{round.name}</div>
          <div className="pr-meta">{sorted.length} nummers &bull; geëxporteerd op {new Date().toLocaleDateString('nl-NL')}</div>
        </div>

        <table className="pr-table">
          <thead>
            <tr>
              <th className="pr-col-nr">#</th>
              <th className="pr-col-title">Titel</th>
              <th className="pr-col-artist">Artiest</th>
              <th className="pr-col-year">Jaar</th>
              <th className="pr-col-source">Bron</th>
              <th className="pr-col-id">ID / URI</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s, i) => (
              <tr key={s.id} className={i % 2 === 0 ? 'pr-row-even' : 'pr-row-odd'}>
                <td className="pr-col-nr">{s.order ?? i + 1}</td>
                <td className="pr-col-title">{s.title || '—'}</td>
                <td className="pr-col-artist">{s.artist || '—'}</td>
                <td className="pr-col-year">{s.year || '—'}</td>
                <td className="pr-col-source">{sourceLabel(s)}</td>
                <td className="pr-col-id pr-id-cell">
                  {s.spotifyUri && (
                    <span className="pr-id spotify-id" title={s.spotifyUri}>
                      {s.spotifyUri.replace('spotify:track:', '')}
                    </span>
                  )}
                  {s.youtubeVideoId && !s.spotifyUri && (
                    <span className="pr-id youtube-id" title={`youtube.com/watch?v=${s.youtubeVideoId}`}>
                      {s.youtubeVideoId}
                      {(s.youtubeStartTime || s.youtubeEndTime) && (
                        <span className="pr-time">
                          {' '}({s.youtubeStartTime ?? 0}s–{s.youtubeEndTime ?? '?'}s)
                        </span>
                      )}
                    </span>
                  )}
                  {s.yearSource && (
                    <span className="pr-yearsource" title={s.yearSource}>
                      {s.yearSource.length > 50 ? s.yearSource.slice(0, 50) + '…' : s.yearSource}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PrintRound;
